/**
 * Inbound protocol-v2 pipeline — authenticate FIRST, then dispatch.
 *
 *  relay/direct/realtime frame
 *    → NIP-01 id check (the signing key is throwaway: it proves nothing)
 *    → open sealed envelope (hybrid KEM to our static key)
 *    → resolve sender: stored contact, or a first-contact PREKEY whose signed
 *      bundle is verified (becomes a *message request*, never auto-trusted)
 *    → session decrypt (AKE authenticates the initiator; the ratchet
 *      authenticates every later message) — on failure NOTHING is persisted
 *    → app payload (stable id, type, body) → handlers
 *
 * Every handler receives the AUTHENTICATED sender. Nothing on the wire (device
 * ids, conversation ids, content types) is trusted any more — those were the
 * root of v1's forged edit/delete authorship and group-author spoofing.
 *
 * Robustness (2026-10 review):
 *  - the decrypted payload is journaled atomically with the ratchet state and
 *    replayed after a crash (M2); handlers are idempotent;
 *  - handled event ids persist across reloads, so relay catch-up re-deliveries are
 *    skipped before any crypto (and never mistaken for a desync);
 *  - a known contact's message that no session can read triggers a rate-limited
 *    session restart + empty sync message, so both sides re-converge (H1);
 *  - a key-change alarm needs an AUTHENTICATED PREKEY under the new key (L3);
 *  - payload ids must carry the sender's identity prefix and inbound rows are
 *    insert-if-absent, so nobody can pre-empt another sender's message (L2);
 *  - envelopes that already failed are remembered by digest (cheap re-rejection, L1).
 */

import {
  applyReaction,
  decodePollWire,
  encodePollWire,
  eventIdOwnedBy,
  evaluateInboundGroupKey,
  listPreview,
  mentionsIdentity,
  mergePollVoter,
  tombstoneEveryone,
  type Account,
  type Contact,
  type Conversation,
  type GroupRecord,
  type MessageEvent,
  type TransportChannel,
} from "@veto/domain";
import {
  CONTENT_TYPE_ATTACHMENT,
  CONTENT_TYPE_SYNC,
  CONTENT_TYPE_DELETE,
  CONTENT_TYPE_EDIT,
  CONTENT_TYPE_GROUP,
  CONTENT_TYPE_PROFILE,
  CONTENT_TYPE_REACTION,
  CONTENT_TYPE_SIGNAL,
  CONTENT_TYPE_TEXT,
  boundedSenderTime,
  canonicalGroupKeyBytes,
  canonicalTransportEventBytes,
  decodeAppPayload,
  decodeGroupPayload,
  decodeProfilePayload,
  decodeSignal,
  decryptAttachmentChunk,
  decryptGroupText,
  decryptProfileAvatar,
  downloadChunksDual,
  encodeSignal,
  envelopeFromEventContent,
  hexToBytes,
  isLegacyV1Content,
  parseInboundAttachment,
  sha256Bytes,
  stickerRefLabel,
  validateGroupKeyAuthorization,
  type AppPayload,
  type ProfileMedia,
} from "@veto/protocol";
import {
  addSecurityEvent,
  bumpInboxAttempts,
  deleteInbox,
  getContact,
  getGroup,
  getGroupByConversation,
  getMessageRaw,
  getMeta,
  listConversations,
  listInbox,
  markEventsProcessed,
  openInboxRow,
  processedAmong,
  saveContact,
  saveGroup,
  setMeta,
  upsertConversation,
} from "@veto/storage";
import { DirectPeerLink, iceServersFromBootstrap, type DirectPeerLink as PeerLink } from "@veto/transport";
import { directLinkAllowed } from "../netPrefs";
import {
  INBOUND_PREKEY,
  fromHex,
  readCompactBundle,
  safetyNumber,
  toHex,
  verifyMessagingSig,
  type CompactBundleKeys,
  type WasmIdentity,
  type WasmInbound,
} from "@veto/crypto-bridge";
import { loadNotificationPrefs, notifyIncomingMessage, type InAppBannerPayload } from "../notifications";
import { blobToDataB64 } from "../profile/profileMedia";
import { messagingDeviceIdHex } from "./sessionDevices";
import { inboundTimer } from "./disappearWire";
import { rememberMediaBlob } from "./mediaBlobs";
import type { PeerKeyBytes, SessionStore } from "./sessionStore";
import { directConversationId, peerKeyBytes } from "./wire";

export interface InboundDeps {
  account: Account;
  identity: WasmIdentity;
  store: SessionStore;
  seenEventIds: Set<string>;
  activeConversationId: string | null;
  contacts: Contact[];
  peers: Map<string, PeerLink>;
  voiceUrls: Map<string, string>;
  /** Arrival channel for UI transparency (NET-REL-006). */
  transportHint?: TransportChannel;
  putMessage: (msg: MessageEvent) => Promise<void>;
  /** Insert a NEW inbound row; false when the id already exists (never overwrites). */
  insertMessage: (msg: MessageEvent) => Promise<boolean>;
  listMessages: (conversationId: string) => Promise<MessageEvent[]>;
  /** Encrypted reply to one contact (WebRTC answer …). */
  sendTo: (contact: Contact, contentType: number, body: string) => Promise<void>;
  /** Frames received on a direct peer link re-enter this pipeline. */
  onPeerFrame: (data: string) => void;
  /** ICE servers for answering a direct-link offer (minted short-lived TURN credentials). */
  iceServers?: () => Promise<RTCIceServer[]>;
  /** False once the session these deps were built for is gone (locked): nothing is marked seen. */
  alive?: () => boolean;
  onToast: (msg: string) => void;
  onBanner: (b: InAppBannerPayload) => void;
  onMessagesAppend: (conversationId: string, msg: MessageEvent) => void;
  onMessagesReload: (conversationId: string) => Promise<void>;
  onActiveGroup: (g: GroupRecord) => void;
  onVoiceUrl: () => void;
  refresh: () => Promise<void>;
}

type RawEvent = {
  id?: unknown;
  pubkey?: unknown;
  created_at?: unknown;
  kind?: unknown;
  tags?: unknown;
  content?: unknown;
  sig?: unknown;
};

/** Authenticated context handed to handlers. */
type Ctx = {
  deps: InboundDeps;
  contact: Contact;
  /** Pairwise conversation with the sender. */
  convId: string;
  app: AppPayload;
  /** Authenticated sender device id (derived from the verified identity key). */
  senderDevice: string;
  createdAt: number;
};

const inflight = new Set<string>();
let legacyNoticeAt = 0;

/** Digests of envelopes that already failed (a re-wrapped copy costs no crypto). */
const failedEnvelopes = new Set<string>();
const FAILED_ENVELOPES_MAX = 2000;
/** Per-conversation throttles (diagnostics writes, session recovery). */
const failNoteAt = new Map<string, number>();
const recoverAt = new Map<string, number>();
const RECOVER_EVERY_MS = 10 * 60_000;

/** Handled ids are persisted in small batches (one write per burst, not per event). */
let processedBatch: string[] = [];
let processedFlush: ReturnType<typeof setTimeout> | null = null;
function noteProcessed(id: string): void {
  processedBatch.push(id);
  if (processedFlush) return;
  processedFlush = setTimeout(() => {
    const ids = processedBatch;
    processedBatch = [];
    processedFlush = null;
    void markEventsProcessed(ids).catch(() => undefined);
  }, 1500);
}

export async function processInboundRelayEvent(ev: unknown, deps: InboundDeps): Promise<void> {
  if (!Array.isArray(ev) || ev[0] !== "EVENT") return;
  const payload = ev[2] as RawEvent | undefined;
  if (!payload || typeof payload.id !== "string" || typeof payload.content !== "string") return;
  // Locked since this delivery started: leave it for the relay copy after unlock (never "seen").
  if (deps.alive && !deps.alive()) return;
  const id = payload.id.toLowerCase();
  // Claim synchronously so concurrent deliveries (several relays) never double-open.
  if (deps.seenEventIds.has(id) || inflight.has(id)) return;
  inflight.add(id);
  try {
    // Handled in an earlier run (relay catch-up re-sends after every reload).
    if ((await processedAmong([id])).size) {
      deps.seenEventIds.add(id);
      return;
    }
    await processClaimed(id, payload, deps);
  } catch (err) {
    console.warn(`veto: drop inbound event: ${err instanceof Error ? err.message : String(err)}`);
  } finally {
    inflight.delete(id);
  }
}

function hasShape(p: RawEvent): p is {
  id: string;
  pubkey: string;
  created_at: number;
  kind: number;
  tags: string[][];
  content: string;
  sig: string;
} {
  return (
    typeof p.id === "string" &&
    /^[0-9a-f]{64}$/i.test(p.id) &&
    typeof p.pubkey === "string" &&
    typeof p.created_at === "number" &&
    typeof p.kind === "number" &&
    typeof p.sig === "string" &&
    Array.isArray(p.tags) &&
    typeof p.content === "string"
  );
}

async function processClaimed(id: string, payload: RawEvent, deps: InboundDeps): Promise<void> {
  const seen = (): void => {
    deps.seenEventIds.add(id);
    noteProcessed(id);
  };
  if (!hasShape(payload)) return seen();
  // Integrity of the frame itself (relays verify the throwaway signature).
  const canonical = toHex(
    await sha256Bytes(
      canonicalTransportEventBytes({
        id: payload.id,
        pubkey: payload.pubkey,
        created_at: payload.created_at,
        kind: payload.kind,
        tags: payload.tags,
        content: payload.content,
      }),
    ),
  );
  if (canonical !== id) return seen();

  const envelope = envelopeFromEventContent(payload.content);
  if (!envelope) {
    if (isLegacyV1Content(payload.content)) noteLegacySender(deps);
    return seen();
  }
  const digest = toHex(await sha256Bytes(envelope));
  if (failedEnvelopes.has(digest)) return seen();

  let inbound: WasmInbound;
  try {
    inbound = deps.identity.openEnvelope(envelope);
  } catch {
    // A local failure (keys freed by a lock) must not burn the event: relays re-deliver it.
    if (deps.alive && !deps.alive()) return;
    return seen(); // not addressed to us, or tampered
  }
  try {
    const ok = await processOpened(id, inbound, deps);
    if (!ok) {
      if (failedEnvelopes.size >= FAILED_ENVELOPES_MAX) failedEnvelopes.clear();
      failedEnvelopes.add(digest);
    }
  } finally {
    inbound.free();
  }
}

/** Journal row: everything needed to (re)run the handlers without the crypto. */
type JournalEntry = {
  v: 1;
  convId: string;
  senderEd: string;
  bundle: CompactBundleKeys | null;
  firstContact: boolean;
  payloadB64: string;
  transport?: TransportChannel;
};

function toB64(bytes: Uint8Array): string {
  let out = "";
  for (let i = 0; i < bytes.length; i++) out += String.fromCharCode(bytes[i]!);
  return btoa(out);
}

function fromB64(text: string): Uint8Array {
  const bin = atob(text);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

async function noteDecryptFailure(convId: string, err: unknown): Promise<void> {
  const now = Date.now();
  if (now - (failNoteAt.get(convId) ?? 0) < 60_000) return; // L1: no write per forged frame
  failNoteAt.set(convId, now);
  await setMeta(`security.decryptfail.${convId}`, `${now}: ${err instanceof Error ? err.message : String(err)}`);
}

/**
 * The contact holds a session we cannot read (or keeps using one we retired):
 * restart ours and send an empty sync message — the peer adopts the restarted
 * session (restart flag) and its next messages arrive again. Never toward a
 * pending request (no automatic traffic to strangers). Rate-limited, so forged
 * frames can at most cause one cheap restart per contact every 10 minutes.
 */
async function maybeRecoverSession(contact: Contact, convId: string, deps: InboundDeps): Promise<void> {
  if (contact.blockedAt || contact.requestState === "pending") return;
  const now = Date.now();
  const last = recoverAt.get(convId) ?? Number((await getMeta(`recover.${convId}`)) ?? 0);
  if (now - last < RECOVER_EVERY_MS) return;
  recoverAt.set(convId, now);
  await setMeta(`recover.${convId}`, String(now));
  await deps.store.restartSession(convId, fromHex(contact.messagingPublicKeyHex));
  await deps.sendTo(contact, CONTENT_TYPE_SYNC, "");
}

/** @returns false when the envelope failed (remember its digest). */
async function processOpened(id: string, inbound: WasmInbound, deps: InboundDeps): Promise<boolean> {
  const seen = (): void => {
    deps.seenEventIds.add(id);
    noteProcessed(id);
  };
  const senderEdHex = toHex(inbound.senderEd).toLowerCase();
  const selfEdHex = deps.account.messagingPublicKeyHex.toLowerCase();
  if (senderEdHex === selfEdHex) {
    seen();
    return false;
  }

  let contact = deps.contacts.find((c) => c.messagingPublicKeyHex.toLowerCase() === senderEdHex);
  let bundleKeys: CompactBundleKeys | null = null;
  if (inbound.kind === INBOUND_PREKEY) {
    const bundle = inbound.bundle;
    bundleKeys = bundle ? readCompactBundle(bundle) : null;
    if (!bundleKeys || bundleKeys.ed !== senderEdHex) {
      seen();
      return false;
    }
  }

  let peer: PeerKeyBytes;
  let firstContact = false;
  if (contact) {
    if (contact.blockedAt) {
      seen();
      return true;
    }
    if (bundleKeys && bundleKeys.hybrid !== contact.hybridPublicKeyHex.toLowerCase()) {
      // Same identity key, different static key: never silently re-key (FR-CON-003).
      // Alarm only if the sender PROVES the new key (AKE succeeds) — a stale or
      // replayed bundle must not flip a contact to "changed" (review L3).
      const claimed = { ed: fromHex(bundleKeys.ed), hybrid: fromHex(bundleKeys.hybrid) };
      const authentic = deps.store.probe(claimed, inbound);
      if (authentic) await recordKeyMismatch(contact, deps);
      seen();
      return authentic;
    }
    peer = peerKeyBytes(contact);
  } else {
    // Unknown sender: only an authenticated first-contact message can start a request.
    if (!bundleKeys) {
      seen();
      return false;
    }
    peer = { ed: fromHex(bundleKeys.ed), hybrid: fromHex(bundleKeys.hybrid) };
    firstContact = true;
  }

  const convId = await directConversationId(selfEdHex, senderEdHex);
  const journalEntry = (pt: Uint8Array): { id: string; json: string } => ({
    id,
    json: JSON.stringify({
      v: 1,
      convId,
      senderEd: senderEdHex,
      bundle: bundleKeys,
      firstContact,
      payloadB64: toB64(pt),
      transport: deps.transportHint,
    } satisfies JournalEntry),
  });
  try {
    await deps.store.decrypt(convId, peer, inbound, journalEntry);
  } catch (err) {
    // Forged, replayed or undecryptable — the session is untouched (transactional core).
    await noteDecryptFailure(convId, err);
    seen();
    const msg = err instanceof Error ? err.message : String(err);
    if (contact && /no session could decrypt|retired session/.test(msg)) {
      void maybeRecoverSession(contact, convId, deps).catch(() => undefined);
    }
    return false;
  }
  seen();
  await runJournaled(id, deps);
  return true;
}

/** Run the handlers for one journal row, then drop it (kept for retry on failure). */
async function runJournaled(id: string, deps: InboundDeps): Promise<void> {
  const row = (await listInbox()).find((r) => r.id === id);
  if (!row) return;
  const json = await openInboxRow(row);
  if (!json) {
    await deleteInbox(id);
    return;
  }
  try {
    await handleDecrypted(JSON.parse(json) as JournalEntry, deps);
    await deleteInbox(id);
  } catch (err) {
    const attempts = await bumpInboxAttempts(id);
    if (attempts >= 5) await deleteInbox(id); // poison row: give up after 5 tries
    console.warn(`veto: inbound handler failed (${attempts}): ${err instanceof Error ? err.message : String(err)}`);
  }
}

/**
 * After unlock: finish messages whose key was consumed but whose handling did
 * not complete (crash, closed tab mid catch-up). Handlers are idempotent.
 */
export async function replayInboundJournal(deps: InboundDeps): Promise<number> {
  const rows = await listInbox();
  for (const row of rows) await runJournaled(row.id, deps);
  return rows.length;
}

async function handleDecrypted(entry: JournalEntry, deps: InboundDeps): Promise<void> {
  if (entry.v !== 1) return;
  let app: AppPayload;
  try {
    app = decodeAppPayload(fromB64(entry.payloadB64));
  } catch {
    return;
  }
  // Ids carry their author's identity prefix: nobody can mint another sender's ids.
  if (!eventIdOwnedBy(app.id, entry.senderEd)) return;
  let contact =
    deps.contacts.find((c) => c.messagingPublicKeyHex.toLowerCase() === entry.senderEd) ??
    (await getContact(entry.senderEd.slice(0, 16)));
  if (contact && contact.messagingPublicKeyHex.toLowerCase() !== entry.senderEd) return;
  if (!contact && entry.firstContact && entry.bundle) {
    contact = await adoptMessageRequest(entry.bundle, entry.convId, app, deps);
    if (!contact) return;
    deps = { ...deps, contacts: [...deps.contacts, contact] };
  } else if (contact && entry.bundle && !contact.transportPublicKeyHex) {
    contact = { ...contact, transportPublicKeyHex: xOnly(entry.bundle.transport) };
    await saveContact(contact);
  }
  if (!contact) return;
  if (entry.transport) deps = { ...deps, transportHint: entry.transport };

  const ctx: Ctx = {
    deps,
    contact,
    convId: entry.convId,
    app,
    senderDevice: messagingDeviceIdHex(contact.messagingPublicKeyHex),
    createdAt: boundedSenderTime(app.at),
  };
  await dispatch(ctx);
}

async function dispatch(ctx: Ctx): Promise<void> {
  switch (ctx.app.t) {
    case CONTENT_TYPE_TEXT:
      return handleText(ctx);
    case CONTENT_TYPE_ATTACHMENT:
      return handleAttachment(ctx);
    case CONTENT_TYPE_GROUP:
      return handleGroup(ctx);
    case CONTENT_TYPE_PROFILE:
      return handleProfile(ctx);
    case CONTENT_TYPE_SIGNAL:
      return handleSignal(ctx);
    case CONTENT_TYPE_REACTION:
    case CONTENT_TYPE_EDIT:
    case CONTENT_TYPE_DELETE:
      return handleControl(ctx);
    case CONTENT_TYPE_SYNC:
      return; // session recovery ping — the decrypt itself did the work
    default:
      return; // unknown/unsupported content type — ignore (forward compatible)
  }
}

function xOnly(hex: string): string {
  const h = hex.toLowerCase();
  return h.length === 66 && (h.startsWith("02") || h.startsWith("03")) ? h.slice(2) : h;
}

function noteLegacySender(deps: InboundDeps): void {
  const now = Date.now();
  if (now - legacyNoticeAt < 6 * 60 * 60 * 1000) return;
  legacyNoticeAt = now;
  deps.onToast("A contact is on an outdated Veto version — ask them to update to keep chatting securely");
}

async function recordKeyMismatch(contact: Contact, deps: InboundDeps): Promise<void> {
  if (contact.verificationState !== "changed") {
    await saveContact({ ...contact, verificationState: "changed" });
    await addSecurityEvent({
      id: `keymismatch.${contact.id}`,
      kind: "key_change",
      contactId: contact.id,
      detail: `${contact.displayName} presented a different encryption key. Messages were blocked until you re-verify their safety number.`,
    });
    deps.onToast(`Security: ${contact.displayName}'s keys changed — verify before continuing`);
    await deps.refresh();
  }
}

/**
 * First contact from someone who holds our invite (FR-CON-001 one-way add).
 * Runs only AFTER the AKE authenticated the sender as the holder of the bundle's
 * keys, so an impersonator can no longer create a contact under a victim's
 * identity (v1 PoC). The contact is a pending *message request*: unverified, no
 * direct P2P, no auto-downloads, no group invites until the user accepts.
 */
async function adoptMessageRequest(
  keys: CompactBundleKeys,
  convId: string,
  app: AppPayload,
  deps: InboundDeps,
): Promise<Contact | undefined> {
  const id = keys.ed.slice(0, 16);
  const existing = await getContact(id);
  // A different identity sharing the 64-bit id prefix must never inherit a contact.
  if (existing) return existing.messagingPublicKeyHex.toLowerCase() === keys.ed ? existing : undefined;
  const claimed = (app.n ?? "").replace(/[\u0000-\u001f\u007f]/g, "").trim().slice(0, 64);
  const contact: Contact = {
    id,
    displayName: claimed || "Unknown",
    messagingPublicKeyHex: keys.ed,
    hybridPublicKeyHex: keys.hybrid,
    transportPublicKeyHex: xOnly(keys.transport),
    safetyNumber: safetyNumber(
      fromHex(deps.account.messagingPublicKeyHex),
      fromHex(keys.ed),
      fromHex(deps.account.hybridPublicKeyHex),
      fromHex(keys.hybrid),
    ),
    verificationState: "unverified",
    requestState: "pending",
    addedVia: "message",
    addedAt: Date.now(),
  };
  await saveContact(contact);
  // A group copy from a member we never added: the group message is judged by the
  // group roster; don't spawn a stray direct "request" chat for it.
  if (app.t === CONTENT_TYPE_GROUP) return contact;
  await addSecurityEvent({
    id: `request.${id}`,
    kind: "message_request",
    contactId: id,
    detail: `${contact.displayName} messaged you but is not in your contacts. Accept, or block to stop them.`,
  });
  await upsertConversation({
    id: convId,
    type: "direct",
    title: contact.displayName,
    contactId: id,
    lastMessagePreview: "Message request",
    lastMessageAt: Date.now(),
    unread: 0,
    pinned: false,
    muted: false,
    requestPending: true,
  });
  return contact;
}

/** Recreate a direct chat row when the user had deleted it but kept the contact. */
async function ensureDirectConversation(ctx: Ctx): Promise<Conversation> {
  const convs = await listConversations();
  const existing = convs.find((c) => c.id === ctx.convId);
  if (existing) return existing;
  const conv: Conversation = {
    id: ctx.convId,
    type: "direct",
    title: ctx.contact.displayName,
    contactId: ctx.contact.id,
    lastMessagePreview: "",
    lastMessageAt: Date.now(),
    unread: 0,
    pinned: false,
    muted: false,
    requestPending: ctx.contact.requestState === "pending" || undefined,
  };
  await upsertConversation(conv);
  return conv;
}

/**
 * Inbound rows never overwrite existing ones: message ids are chosen by the
 * sender and the id is the global primary key, so a hostile peer could
 * otherwise replace any message it knows the id of.
 */
async function isFreshMessageId(eventId: string): Promise<boolean> {
  return (await getMessageRaw(eventId)) === undefined;
}

async function bumpConversationForInbound(
  conversationId: string,
  previewText: string,
  deps: InboundDeps,
): Promise<Conversation | undefined> {
  const conv = (await listConversations()).find((c) => c.id === conversationId);
  if (!conv) return undefined;
  const preview = listPreview(previewText) || previewText;
  await upsertConversation({
    ...conv,
    lastMessagePreview: preview,
    lastMessageAt: Date.now(),
    // Archived chats stay archived when new messages arrive (phone parity).
    archived: conv.archived,
    unread:
      deps.activeConversationId === conversationId ? 0 : conv.muted ? conv.unread : conv.unread + 1,
  });
  return conv;
}

async function notifyFor(
  deps: InboundDeps,
  conv: Conversation | undefined,
  conversationId: string,
  name: string | undefined,
  preview: string,
): Promise<void> {
  if (deps.activeConversationId === conversationId || conv?.muted) return;
  const bannerPayload = await notifyIncomingMessage({
    conversationId,
    contactName: conv?.requestPending ? `${name ?? "Someone"} (request)` : name,
    bodyPreview: preview,
    activeConversationId: deps.activeConversationId,
    prefs: loadNotificationPrefs(),
    locked: await isChatLocked(conversationId),
  });
  if (bannerPayload) deps.onBanner(bannerPayload);
}

/** A chat with a chat lock: notifications say nothing about it. */
export async function isChatLocked(conversationId: string): Promise<boolean> {
  return !!(await getMeta(`chatlock.${conversationId}`));
}

/** Group invitations: while one waits for your answer, only its sender can update the group. */
const MAX_OPEN_INVITES_PER_CONTACT = 5;

async function openInvitesFrom(contactId: string): Promise<number> {
  let n = 0;
  for (const c of await listConversations()) {
    if (c.type === "group" && c.requestPending && (await getMeta(`group.pending.${c.id}`)) === contactId) n++;
  }
  return n;
}

/**
 * A vote re-sends the whole poll under a NEW event id. When we already show that poll,
 * fold the sender's vote into the existing card instead of adding another one.
 */
async function mergePollSnapshot(deps: InboundDeps, conversationId: string, body: string, senderDevice: string): Promise<boolean> {
  const snapshot = decodePollWire(body);
  if (!snapshot) return false;
  const existing = (await deps.listMessages(conversationId)).find(
    (m) => !m.deletedForEveryone && decodePollWire(m.body)?.pollId === snapshot.pollId,
  );
  const local = existing ? decodePollWire(existing.body) : null;
  if (!existing || !local) return false;
  await deps.putMessage({ ...existing, body: encodePollWire(mergePollVoter(local, snapshot, senderDevice)) });
  await deps.onMessagesReload(conversationId);
  await deps.refresh();
  return true;
}

// ---------------------------------------------------------------------------
// Handlers
// ---------------------------------------------------------------------------

async function handleText(ctx: Ctx): Promise<void> {
  const { deps, app } = ctx;
  if (!(await isFreshMessageId(app.id))) return; // cheap early exit; insert below is authoritative
  const conv = await ensureDirectConversation(ctx);
  const msg: MessageEvent = {
    eventId: app.id,
    conversationId: ctx.convId,
    senderDevice: ctx.senderDevice,
    direction: "in",
    transport: deps.transportHint,
    body: app.b,
    logicalCounter: ctx.createdAt,
    createdAt: ctx.createdAt,
    status: "delivered",
    contentType: CONTENT_TYPE_TEXT,
  };
  let plain = app.b;
  if (plain.startsWith("\u0001")) {
    const parts = plain.split("\u0001");
    if (parts.length >= 3 && /^[0-9a-f]{64}$/.test(parts[1] ?? "")) {
      msg.replyToEventId = parts[1];
      plain = parts.slice(2).join("\u0001");
    }
  }
  if (plain.startsWith("\u0002")) {
    const parts = plain.split("\u0002");
    if (parts.length >= 3) {
      const timer = Number(parts[1]) || 0;
      msg.disappearTimerSec = timer > 0 && timer <= 90 * 86400 ? timer : undefined;
      msg.expiresAt = msg.disappearTimerSec ? Date.now() + msg.disappearTimerSec * 1000 : undefined;
      plain = parts.slice(2).join("\u0002");
    }
  }
  msg.body = plain;
  if (await mergePollSnapshot(deps, ctx.convId, plain, ctx.senderDevice)) return;
  if (!(await deps.insertMessage(msg))) return;
  const bumped = await bumpConversationForInbound(ctx.convId, msg.body, deps);
  if (deps.activeConversationId === ctx.convId) deps.onMessagesAppend(ctx.convId, msg);
  else await notifyFor(deps, bumped ?? conv, ctx.convId, ctx.contact.displayName, msg.body);
  await deps.refresh();
}

async function handleControl(ctx: Ctx): Promise<void> {
  const { deps, app } = ctx;
  let op: { op?: unknown; target?: unknown; emoji?: unknown; body?: unknown };
  try {
    op = JSON.parse(app.b) as typeof op;
  } catch {
    return;
  }
  if (typeof op.target !== "string" || !/^[0-9a-f]{64}$/.test(op.target)) return;
  // The target may still be committing when ops race it — brief bounded retry.
  let existing: MessageEvent | undefined;
  for (let attempt = 0; attempt < 6; attempt++) {
    existing = (await deps.listMessages(ctx.convId)).find((m) => m.eventId === op.target);
    if (existing) break;
    await new Promise((r) => setTimeout(r, 80 + attempt * 40));
  }
  if (!existing) return;
  let next: MessageEvent | null = null;
  if (app.t === CONTENT_TYPE_REACTION && op.op === "reaction" && typeof op.emoji === "string" && op.emoji.length <= 16) {
    next = applyReaction(existing, op.emoji, ctx.senderDevice);
  } else if (app.t === CONTENT_TYPE_EDIT && op.op === "edit" && typeof op.body === "string") {
    // Only the AUTHENTICATED original author may edit.
    if (existing.direction !== "in" || existing.senderDevice !== ctx.senderDevice) return;
    next = { ...existing, body: op.body.slice(0, 64_000), editedAt: Date.now() };
  } else if (app.t === CONTENT_TYPE_DELETE && op.op === "delete") {
    if (existing.direction !== "in" || existing.senderDevice !== ctx.senderDevice) return;
    next = tombstoneEveryone(existing);
  }
  if (!next) return;
  await deps.putMessage(next);
  await deps.onMessagesReload(ctx.convId);
  await deps.refresh();
}

async function handleAttachment(ctx: Ctx): Promise<void> {
  const { deps, app } = ctx;
  if (!(await isFreshMessageId(app.id))) return;
  const att = parseInboundAttachment(app.b);
  if (!att) return;
  const conv = await ensureDirectConversation(ctx);
  const isVoice = att.kind === "voice" || Boolean(att.manifest?.mediaType?.startsWith("audio/"));
  const isViewOnce = att.kind === "view-once";
  const builtinSticker =
    att.op === "sticker" &&
    typeof att.pack === "string" &&
    typeof att.id === "string" &&
    /^[a-z0-9.-]+$/.test(att.pack) &&
    /^[a-z0-9-]+$/.test(att.id)
      ? { pack: att.pack, id: att.id }
      : null;
  const seconds = Math.max(1, Math.round((att.durationMs ?? 1000) / 1000));
  const fileName = (att.manifest?.fileName ?? "attachment").replace(/[\u0000-\u001f\u007f/\\]/g, "_");
  const label = builtinSticker
    ? stickerRefLabel(builtinSticker.pack, builtinSticker.id)
    : isVoice
      ? `🎤 Voice note (${seconds}s)`
      : isViewOnce
        ? "👁 View once media"
        : att.kind === "sticker"
          ? "🏷️ Sticker"
          : att.kind === "gif"
            ? `🎞️ ${fileName}`
            : `📎 ${fileName}`;

  const msg: MessageEvent = {
    eventId: app.id,
    conversationId: ctx.convId,
    senderDevice: ctx.senderDevice,
    direction: "in",
    transport: deps.transportHint,
    body: label,
    logicalCounter: ctx.createdAt,
    createdAt: ctx.createdAt,
    status: "delivered",
    contentType: CONTENT_TYPE_ATTACHMENT,
    viewOnce: isViewOnce || undefined,
  };
  // The sender's disappearing timer (phone parity, review M3): purge here too.
  const timer = inboundTimer(att.disappearSeconds);
  if (timer) {
    msg.disappearTimerSec = timer;
    msg.expiresAt = Date.now() + timer * 1000;
  }
  // Store the row BEFORE any network download (review M2): a slow or failed
  // fetch can no longer cost the message itself.
  if (!(await deps.insertMessage(msg))) return;
  const bumped = await bumpConversationForInbound(ctx.convId, label, deps);
  if (deps.activeConversationId === ctx.convId) deps.onMessagesAppend(ctx.convId, msg);
  else await notifyFor(deps, bumped ?? conv, ctx.convId, ctx.contact.displayName, label);
  await deps.refresh();

  if (att.manifest?.fileKeyB64) {
    try {
      let packed: Uint8Array[] = [];
      if (att.inlineChunksB64?.length) {
        packed = att.inlineChunksB64.map((b64) => {
          const bin = atob(b64);
          const out = new Uint8Array(bin.length);
          for (let j = 0; j < bin.length; j++) out[j] = bin.charCodeAt(j);
          return out;
        });
      } else if (att.manifest.chunks?.length && ctx.contact.requestState !== "pending") {
        // No automatic fetches for message requests (tracking / abuse surface).
        packed = await downloadChunksDual(att.manifest.chunks);
      }
      if (packed.length) {
        const parts: Uint8Array[] = [];
        for (let i = 0; i < packed.length; i++) {
          parts.push(await decryptAttachmentChunk(packed[i]!, att.manifest.fileKeyB64, i, att.manifest.totalSize ?? 0));
        }
        const blob = new Blob(parts as BlobPart[], {
          type: att.manifest.mediaType || (isVoice ? "audio/webm" : "application/octet-stream"),
        });
        deps.voiceUrls.set(app.id, URL.createObjectURL(blob));
        rememberMediaBlob(app.id, blob);
        deps.onVoiceUrl();
      }
    } catch {
      /* preview optional when stores are offline or chunks fail verification */
    }
  }
}

/**
 * Encrypted profile update (name / bio / avatar). The sender is authenticated by
 * the session, so only the contact itself can change its own profile.
 */
async function handleProfile(ctx: Ctx): Promise<void> {
  const { deps, contact } = ctx;
  try {
    const payload = decodeProfilePayload(ctx.app.b);
    const current = await getContact(contact.id);
    if (!current) return;
    if (payload.updatedAt <= (current.profileUpdatedAt ?? 0)) return;
    let avatar: ProfileMedia | null = current.avatar ?? null;
    if (payload.avatar && current.requestState !== "pending") {
      try {
        const blob = await decryptProfileAvatar(payload.avatar);
        avatar = {
          kind: payload.avatar.kind,
          mime: blob.type || payload.avatar.mime,
          dataB64: await blobToDataB64(blob),
          updatedAt: payload.updatedAt,
        };
      } catch {
        /* keep previous avatar when the media store / decrypt fails */
      }
    } else if (payload.avatar === null) {
      avatar = null;
    }
    const displayName = payload.displayName || current.displayName;
    await saveContact({ ...current, displayName, about: payload.about, avatar, profileUpdatedAt: payload.updatedAt });
    const conv = (await listConversations()).find((c) => c.id === ctx.convId);
    if (conv && conv.title !== displayName) await upsertConversation({ ...conv, title: displayName });
    await deps.refresh();
  } catch {
    /* malformed profile update — ignore */
  }
}

/** Offers older than this aren't answered: they sat in an outbox, and answering late would reveal
 *  this device's network addresses whenever the peer next came online. */
export const SIGNAL_MAX_AGE_MS = 2 * 60_000;

/** Two offers this close together crossed in flight (true glare). An older unanswered offer of
 *  ours was evidently dropped by the peer (it hadn't verified us yet), so theirs wins. */
const GLARE_WINDOW_MS = 3_000;

async function handleSignal(ctx: Ctx): Promise<void> {
  const { deps, contact } = ctx;
  if (!directLinkAllowed(contact)) return;
  const signal = decodeSignal(ctx.app.b);
  if (!signal) return;
  if (signal.op === "webrtc-offer") {
    if (Date.now() - ctx.createdAt > SIGNAL_MAX_AGE_MS) return;
    const existing = deps.peers.get(ctx.convId);
    // Glare (both opened the chat at once): the side with the larger key keeps its own offer and
    // ignores this one; the other side yields and answers. Exactly one link forms.
    if (
      existing?.role === "offer" &&
      existing.state === "connecting" &&
      Date.now() - existing.startedAt < GLARE_WINDOW_MS &&
      deps.account.messagingPublicKeyHex.toLowerCase() > contact.messagingPublicKeyHex.toLowerCase()
    ) {
      return;
    }
    existing?.close();
    const ice = deps.iceServers ? await deps.iceServers().catch(() => iceServersFromBootstrap()) : iceServersFromBootstrap();
    const convId = ctx.convId;
    const link = new DirectPeerLink(ice, {
      onMessage: (data) => deps.onPeerFrame(data),
      onState: (s) => {
        if ((s === "failed" || s === "closed") && deps.peers.get(convId) === link) deps.peers.delete(convId);
      },
    });
    deps.peers.set(convId, link);
    try {
      const answer = await link.acceptOffer({ type: "offer", sdp: signal.sdp });
      await deps.sendTo(contact, CONTENT_TYPE_SIGNAL, encodeSignal({ op: "webrtc-answer", sdp: answer.sdp ?? "" }));
    } catch {
      link.close();
      if (deps.peers.get(convId) === link) deps.peers.delete(convId);
    }
  } else if (signal.op === "webrtc-answer") {
    const link = deps.peers.get(ctx.convId);
    if (link) {
      try {
        await link.applyAnswer({ type: "answer", sdp: signal.sdp });
      } catch {
        /* malformed answer — the link times out; relays keep working */
      }
    }
  }
}

async function handleGroup(ctx: Ctx): Promise<void> {
  const { deps, contact } = ctx;
  const gp = decodeGroupPayload(ctx.app.b);
  if (!gp) return;
  const senderHex = contact.messagingPublicKeyHex.toLowerCase();

  if (gp.op === "group-key") {
    if (contact.requestState === "pending") {
      deps.onToast(`${contact.displayName} tried to add you to a group — accept their request first`);
      return;
    }
    if (!gp.adminIdentityKeyHex || !gp.adminSigHex) return;
    // The admin who signed must be the authenticated sender of this copy.
    if (gp.adminIdentityKeyHex.toLowerCase() !== senderHex) {
      await setMeta(`group.reject.${gp.groupId}`, "group-key relayed by a non-signer");
      return;
    }
    // Review M1 (phone parity): a group you haven't joined is an invitation. While it waits,
    // only the contact who sent it may update it; at most 5 open invitations per contact.
    const pendingFrom = await getMeta(`group.pending.${gp.groupId}`);
    if (pendingFrom && pendingFrom !== contact.id) return;
    const prior = await getGroup(gp.groupId);
    if (!prior && (await openInvitesFrom(contact.id)) >= MAX_OPEN_INVITES_PER_CONTACT) return;
    const authz = validateGroupKeyAuthorization({
      payload: {
        op: "group-key",
        groupId: gp.groupId,
        epoch: gp.epoch,
        title: gp.title,
        members: gp.members,
        senderKeyB64: gp.senderKeyB64,
        adminIdentityKeyHex: gp.adminIdentityKeyHex,
        adminSigHex: gp.adminSigHex,
      },
      prior: prior ? { epoch: prior.epoch, members: prior.members } : null,
    });
    if (!authz.ok) {
      await setMeta(`group.reject.${gp.groupId}`, authz.reason);
      return;
    }
    const canon = canonicalGroupKeyBytes({
      groupId: gp.groupId,
      epoch: gp.epoch,
      title: gp.title,
      members: gp.members,
      senderKeyB64: gp.senderKeyB64,
      adminIdentityKeyHex: gp.adminIdentityKeyHex,
    });
    if (!verifyMessagingSig(hexToBytes(gp.adminIdentityKeyHex), canon, hexToBytes(gp.adminSigHex))) {
      await setMeta(`group.reject.${gp.groupId}`, "invalid admin signature");
      return;
    }
    // Bounded epoch jump: a rogue co-admin must not wedge the group at epoch 2^53.
    if (prior && gp.epoch > prior.epoch + 1000) {
      await setMeta(`group.reject.${gp.groupId}`, "implausible epoch jump");
      return;
    }
    const decision = evaluateInboundGroupKey({
      prior,
      epoch: gp.epoch,
      members: gp.members,
      senderIdentityKeyHex: gp.adminIdentityKeyHex,
      senderKeyB64: gp.senderKeyB64,
    });
    if (!decision.ok) {
      if (decision.fork) {
        await setMeta(
          `group.fork.${gp.groupId}`,
          `${decision.reason} (local epoch ${prior?.epoch ?? "none"} vs inbound ${gp.epoch})`,
        );
        deps.onToast("Group membership conflict — kept the previous key; see group details");
      }
      await deps.refresh();
      return;
    }
    if (decision.mode === "idempotent") {
      await deps.refresh();
      return;
    }
    const members = gp.members.map((m) => {
      const c = deps.contacts.find(
        (x) => x.messagingPublicKeyHex.toLowerCase() === m.identityKeyHex.toLowerCase(),
      );
      const priorM = prior?.members.find((p) => p.identityKeyHex.toLowerCase() === m.identityKeyHex.toLowerCase());
      return {
        contactId: c?.id ?? priorM?.contactId ?? m.identityKeyHex.slice(0, 16),
        identityKeyHex: m.identityKeyHex,
        hybridPublicKeyHex: c?.hybridPublicKeyHex ?? priorM?.hybridPublicKeyHex ?? "",
        displayName: m.displayName || c?.displayName || "Member",
        role: m.role,
      };
    });
    const record: GroupRecord = {
      groupId: gp.groupId,
      title: gp.title,
      epoch: gp.epoch,
      members,
      senderKeyB64: gp.senderKeyB64,
      createdAt: prior?.createdAt ?? Date.now(),
      conversationId: gp.groupId,
    };
    await saveGroup(record);
    const existingConv = (await listConversations()).find((c) => c.id === record.conversationId);
    if (!existingConv) await setMeta(`group.pending.${record.groupId}`, contact.id);
    await upsertConversation({
      id: record.conversationId,
      type: "group",
      title: record.title,
      lastMessagePreview: existingConv ? existingConv.lastMessagePreview : `${contact.displayName} invited you`,
      lastMessageAt: Date.now(),
      unread: deps.activeConversationId === record.conversationId ? 0 : (existingConv?.unread ?? 0) + (existingConv ? 0 : 1),
      pinned: existingConv?.pinned ?? false,
      muted: existingConv?.muted ?? false,
      archived: existingConv?.archived,
      // First sight of a group = an invitation (Join / Decline / Decline & block).
      requestPending: existingConv ? existingConv.requestPending : true,
    });
    if (deps.activeConversationId === record.conversationId) deps.onActiveGroup(record);
    await deps.refresh();
    return;
  }

  if (gp.op === "group-msg") {
    if (!(await isFreshMessageId(ctx.app.id))) return;
    const group = (await getGroup(gp.groupId)) ?? (await getGroupByConversation(gp.groupId));
    if (!group) return;
    // Authorization: the AUTHENTICATED sender must be a current member.
    const isMember = group.members.some((m) => m.identityKeyHex.toLowerCase() === senderHex);
    if (!isMember) {
      await setMeta(`group.reject.${gp.groupId}`, "sender not a member");
      return;
    }
    if (group.epoch !== gp.epoch) {
      await deps.insertMessage({
        eventId: ctx.app.id,
        conversationId: group.conversationId,
        senderDevice: ctx.senderDevice,
        direction: "in",
        transport: deps.transportHint,
        body: "[Could not decrypt — sent for a different group key epoch]",
        logicalCounter: ctx.createdAt,
        createdAt: ctx.createdAt,
        status: "delivered",
        contentType: CONTENT_TYPE_GROUP,
      });
      await deps.refresh();
      return;
    }
    let plain: string;
    try {
      plain = await decryptGroupText(group.senderKeyB64, gp.ctB64, `${gp.groupId}:${gp.epoch}`);
    } catch {
      return;
    }
    if (await mergePollSnapshot(deps, group.conversationId, plain, ctx.senderDevice)) return;
    const msg: MessageEvent = {
      eventId: ctx.app.id,
      conversationId: group.conversationId,
      senderDevice: ctx.senderDevice,
      direction: "in",
      transport: deps.transportHint,
      body: plain,
      logicalCounter: ctx.createdAt,
      createdAt: ctx.createdAt,
      status: "delivered",
      contentType: CONTENT_TYPE_GROUP,
      replyToEventId:
        typeof gp.replyToEventId === "string" && /^[0-9a-f]{64}$/.test(gp.replyToEventId)
          ? gp.replyToEventId
          : undefined,
      senderContactId: contact.id,
    };
    if (!(await deps.insertMessage(msg))) return;
    // Your own name for the sender (never the admin-supplied one), like the phone.
    const conv = await bumpConversationForInbound(group.conversationId, `${contact.displayName}: ${listPreview(plain) || plain}`, deps);
    if (deps.activeConversationId === group.conversationId) {
      deps.onMessagesAppend(group.conversationId, msg);
    } else {
      const locked = await isChatLocked(group.conversationId);
      // @mentions reach you even in a muted group (phone parity), never in a locked chat.
      const mentioned =
        !locked && mentionsIdentity(plain, group.members, deps.account.messagingPublicKeyHex, [deps.account.displayName]);
      if (!conv?.muted || mentioned) {
        const bannerPayload = await notifyIncomingMessage({
          conversationId: group.conversationId,
          contactName: group.title,
          bodyPreview: mentioned ? `${contact.displayName} mentioned you: ${plain}` : `${contact.displayName}: ${plain}`,
          activeConversationId: deps.activeConversationId,
          prefs: loadNotificationPrefs(),
          locked,
        });
        if (bannerPayload) deps.onBanner(bannerPayload);
      }
    }
    await deps.refresh();
  }
}

export type { Conversation };
